import axios from 'axios';
import { getUser } from './authService';

// Use proxy in development (configured in package.json) or direct URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';
console.log('API Base URL:', API_BASE_URL);
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor - Log before API call
apiClient.interceptors.request.use(
  (config) => {
    const u = getUser();
    if (u?.email) {
      config.headers = config.headers || {};
      config.headers['X-User-Email'] = u.email;
    }
    const timestamp = new Date().toISOString();
    console.log('=== API REQUEST START ===');
    console.log('Timestamp:', timestamp);
    console.log('Method:', config.method?.toUpperCase());
    console.log('URL:', config.baseURL + config.url);
    console.log('Full URL:', config.url);
    console.log('Headers:', config.headers);
    console.log('Params:', config.params);
    console.log('Data:', config.data);
    console.log('========================');
    return config;
  },
  (error) => {
    console.error('=== API REQUEST ERROR ===');
    console.error('Request Error:', error);
    console.error('========================');
    return Promise.reject(error);
  }
);

// Response interceptor - Log after API call
apiClient.interceptors.response.use(
  (response) => {
    const timestamp = new Date().toISOString();
    console.log('=== API RESPONSE SUCCESS ===');
    console.log('Timestamp:', timestamp);
    console.log('Status:', response.status, response.statusText);
    console.log('URL:', response.config.baseURL + response.config.url);
    console.log('Response Headers:', response.headers);
    console.log('Response Data:', response.data);
    console.log('===========================');
    return response;
  },
  (error) => {
    const timestamp = new Date().toISOString();
    console.error('=== API RESPONSE ERROR ===');
    console.error('Timestamp:', timestamp);
    
    if (error.response) {
      // Server responded with error status
      console.error('Status:', error.response.status, error.response.statusText);
      console.error('URL:', error.config?.baseURL + error.config?.url);
      console.error('Response Headers:', error.response.headers);
      console.error('Response Data:', error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      console.error('No response received from server');
      console.error('Request URL:', error.config?.baseURL + error.config?.url);
      console.error('Request Config:', error.config);
    } else {
      // Error in setting up the request
      console.error('Request Setup Error:', error.message);
    }
    console.error('Error Object:', error);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('==========================');
    return Promise.reject(error);
  }
);

export const loginApi = async (email, password) => {
  const response = await apiClient.post('/auth/login', { email, password });
  return response.data;
};

export const registerApi = async (name, organisationName, email, password) => {
  const response = await apiClient.post('/auth/register', { name, organisationName, email, password });
  return response.data;
};

export const fetchSolarData = async (params = {}) => {
  const functionName = 'fetchSolarData';
  const endpoint = '/solarmonitor';
  
  try {
    console.log(`[${functionName}] Starting API call to: ${endpoint}`);
    console.log(`[${functionName}] Full URL will be: ${API_BASE_URL}${endpoint}`);
    
    const mergedParams = { ...params };
    if (process.env.REACT_APP_SHOW_API_DETAIL === 'true') {
      mergedParams.apiDetail = '1';
    }
    const response = await apiClient.get(endpoint, { params: mergedParams });
    
    console.log(`[${functionName}] API call successful`);
    console.log(`[${functionName}] Response status: ${response.status}`);
    console.log(`[${functionName}] Response data received:`, response.data);
    
    return response.data;
  } catch (error) {
    console.error(`[${functionName}] API call failed`);
    console.error(`[${functionName}] Error type:`, error.constructor.name);
    console.error(`[${functionName}] Error message:`, error.message);
    
    if (error.response) {
      console.error(`[${functionName}] Response status:`, error.response.status);
      console.error(`[${functionName}] Response data:`, error.response.data);
    } else if (error.request) {
      console.error(`[${functionName}] No response received. Request details:`, error.request);
    }
    
    console.error(`[${functionName}] Full error object:`, error);
    throw error;
  }
};

export const createOrganizationApi = async (payload) => {
  // Normalize payload to match backend DTO and ensure numeric ids.
  // This avoids ASP.NET Core model-binding failures like:
  // - "The request field is required."
  // - "$.hubId could not be converted to Nullable<Int32>"
  const p = payload || {};
  const normalized = {
    Name: p.Name ?? p.name ?? '',
    OrganisationId:
      p.OrganisationId ?? p.organisationId ?? p.organizationId ?? p.OrganizationId,
    HubId: p.HubId ?? p.hubId ?? p.hid,
    HubIds: p.HubIds ?? p.hubIds,
    Description: p.Description ?? p.description,
    ContactEmail: p.ContactEmail ?? p.contactEmail,
    ContactPhone: p.ContactPhone ?? p.contactPhone,
    Address: p.Address ?? p.address,
    IsActive: p.IsActive ?? p.isActive,
  };

  if (normalized.OrganisationId !== undefined && normalized.OrganisationId !== null && normalized.OrganisationId !== '') {
    normalized.OrganisationId = Number(normalized.OrganisationId);
  }
  if (normalized.HubId !== undefined && normalized.HubId !== null && normalized.HubId !== '') {
    normalized.HubId = Number(normalized.HubId);
  }
  if (Array.isArray(normalized.HubIds)) {
    normalized.HubIds = normalized.HubIds
      .map((x) => Number(x))
      .filter((n) => Number.isFinite(n) && n > 0);
  } else {
    normalized.HubIds = undefined;
  }

  // Force JSON string body to avoid any implicit serialization quirks.
  const response = await apiClient.post('/Organization', JSON.stringify(normalized), {
    headers: { 'Content-Type': 'application/json' },
  });
  return response.data;
};

export const getOrganizationsApi = async () => {
  const response = await apiClient.get('/Organization');
  return response.data;
};

export const getUsersApi = async () => {
  const response = await apiClient.get('/auth/users');
  return response.data;
};

function normalizeOrganizationPayload(p) {
  const normalized = {
    Name: p.Name ?? p.name,
    OrganisationId: p.OrganisationId ?? p.organisationId ?? p.organizationId ?? p.OrganizationId,
    HubId: p.HubId ?? p.hubId ?? p.hid,
    HubIds: p.HubIds ?? p.hubIds,
    Description: p.Description ?? p.description,
    ContactEmail: p.ContactEmail ?? p.contactEmail,
    ContactPhone: p.ContactPhone ?? p.contactPhone,
    Address: p.Address ?? p.address,
    IsActive: p.IsActive ?? p.isActive
  };

  if (normalized.OrganisationId !== undefined && normalized.OrganisationId !== null && normalized.OrganisationId !== '') {
    normalized.OrganisationId = Number(normalized.OrganisationId);
  }
  if (normalized.HubId !== undefined && normalized.HubId !== null && normalized.HubId !== '') {
    normalized.HubId = Number(normalized.HubId);
  }
  if (Array.isArray(normalized.HubIds)) {
    normalized.HubIds = normalized.HubIds.map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0);
  } else {
    normalized.HubIds = undefined;
  }

  Object.keys(normalized).forEach((k) => {
    if (normalized[k] === undefined) delete normalized[k];
  });

  return normalized;
}

export const updateOrganizationApi = async (id, payload) => {
  const normalized = normalizeOrganizationPayload(payload);
  const response = await apiClient.put(`/Organization/${id}`, JSON.stringify(normalized), {
    headers: { 'Content-Type': 'application/json' }
  });
  return response.data;
};

export const deleteOrganizationApi = async (id) => {
  await apiClient.delete(`/Organization/${id}`);
};

export const updateUserApi = async (id, payload) => {
  const p = payload || {};
  const body = {
    Name: p.Name ?? p.name,
    OrganisationName: p.OrganisationName ?? p.organisationName,
    Email: p.Email ?? p.email,
    Role: p.Role ?? p.role,
    IsSuperAdmin: p.IsSuperAdmin ?? p.isSuperAdmin,
    Password: p.Password ?? p.password
  };
  if (body.Password === '' || body.Password == null) delete body.Password;
  const response = await apiClient.put(`/auth/users/${id}`, body);
  return response.data;
};

export const deleteUserApi = async (id) => {
  await apiClient.delete(`/auth/users/${id}`);
};

export const updateProfileApi = async (name, organisationName, email) => {
  const response = await apiClient.put('/auth/profile', { name, organisationName, email });
  return response.data;
};

export const pushDeviceData = async (params) => {
  const functionName = 'pushDeviceData';
  const endpoint = '/DeviceDatas/PushData';
  
  try {
    console.log(`[${functionName}] Starting API call to: ${endpoint}`);
    console.log(`[${functionName}] Input parameters:`, params);
    
    const queryParams = new URLSearchParams();
    queryParams.append('oid', params.oid);
    if (params.hid !== undefined && params.hid !== null) queryParams.append('hid', params.hid);
    if (params.kp1 !== undefined && params.kp1 !== null) queryParams.append('kp1', params.kp1);
    if (params.kp2 !== undefined && params.kp2 !== null) queryParams.append('kp2', params.kp2);
    if (params.kp3 !== undefined && params.kp3 !== null) queryParams.append('kp3', params.kp3);
    if (params.inp4 !== undefined && params.inp4 !== null) queryParams.append('inp4', params.inp4);
    if (params.inp5 !== undefined && params.inp5 !== null) queryParams.append('inp5', params.inp5);
    if (params.inp6 !== undefined && params.inp6 !== null) queryParams.append('inp6', params.inp6);
    if (params.c1 !== undefined && params.c1 !== null) queryParams.append('c1', params.c1);
    if (params.c2 !== undefined && params.c2 !== null) queryParams.append('c2', params.c2);
    if (params.c3 !== undefined && params.c3 !== null) queryParams.append('c3', params.c3);
    if (params.t1 !== undefined && params.t1 !== null) queryParams.append('t1', params.t1);
    if (params.t2 !== undefined && params.t2 !== null) queryParams.append('t2', params.t2);
    if (params.bt !== undefined && params.bt !== null) queryParams.append('bt', params.bt);
    if (params.ph1 !== undefined && params.ph1 !== null) queryParams.append('ph1', params.ph1);
    if (params.ph2 !== undefined && params.ph2 !== null) queryParams.append('ph2', params.ph2);
    if (params.ph3 !== undefined && params.ph3 !== null) queryParams.append('ph3', params.ph3);
    if (params.ph4 !== undefined && params.ph4 !== null) queryParams.append('ph4', params.ph4);
    if (params.ph5 !== undefined && params.ph5 !== null) queryParams.append('ph5', params.ph5);
    if (params.ph6 !== undefined && params.ph6 !== null) queryParams.append('ph6', params.ph6);
    if (params.ph7 !== undefined && params.ph7 !== null) queryParams.append('ph7', params.ph7);
    if (params.ph8 !== undefined && params.ph8 !== null) queryParams.append('ph8', params.ph8);
    if (params.ph9 !== undefined && params.ph9 !== null) queryParams.append('ph9', params.ph9);
    if (params.ph10 !== undefined && params.ph10 !== null) queryParams.append('ph10', params.ph10);
    if (params.ph11 !== undefined && params.ph11 !== null) queryParams.append('ph11', params.ph11);
    if (params.ph12 !== undefined && params.ph12 !== null) queryParams.append('ph12', params.ph12);
    if (params.ph13 !== undefined && params.ph13 !== null) queryParams.append('ph13', params.ph13);
    if (params.ph14 !== undefined && params.ph14 !== null) queryParams.append('ph14', params.ph14);
    if (params.ph15 !== undefined && params.ph15 !== null) queryParams.append('ph15', params.ph15);
    if (params.ph16 !== undefined && params.ph16 !== null) queryParams.append('ph16', params.ph16);
    if (params.ph17 !== undefined && params.ph17 !== null) queryParams.append('ph17', params.ph17);
    if (params.time !== undefined && params.time !== null && params.time !== '') queryParams.append('time', params.time);
    if (params.l !== undefined && params.l !== null && params.l !== '') queryParams.append('l', params.l);

    const queryString = queryParams.toString();
    const fullUrl = `${API_BASE_URL}${endpoint}?${queryString}`;
    console.log(`[${functionName}] Query string: ${queryString}`);
    console.log(`[${functionName}] Full URL will be: ${fullUrl}`);

    const response = await apiClient.get(`${endpoint}?${queryString}`);
    
    console.log(`[${functionName}] API call successful`);
    console.log(`[${functionName}] Response status: ${response.status}`);
    console.log(`[${functionName}] Response data received:`, response.data);
    
    return response.data;
  } catch (error) {
    console.error(`[${functionName}] API call failed`);
    console.error(`[${functionName}] Error type:`, error.constructor.name);
    console.error(`[${functionName}] Error message:`, error.message);
    
    if (error.response) {
      console.error(`[${functionName}] Response status:`, error.response.status);
      console.error(`[${functionName}] Response data:`, error.response.data);
    } else if (error.request) {
      console.error(`[${functionName}] No response received. Request details:`, error.request);
    }
    
    console.error(`[${functionName}] Full error object:`, error);
    throw error;
  }
};

export const exportCsvReport = async (params) => {
  const functionName = 'exportCsvReport';
  // Match the case used in other endpoints
  const endpoint = '/solarmonitor/export-csv';
  
  try {
    console.log(`[${functionName}] Starting CSV export with parameters:`, params);
    
    // Build query parameters
    const queryParams = {};
    if (params.organizationId !== undefined && params.organizationId !== null) {
      queryParams.organizationId = params.organizationId;
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.startTime) {
      queryParams.startTime = params.startTime;
    }
    if (params.endTime) {
      queryParams.endTime = params.endTime;
    }

    const fullUrl = `${API_BASE_URL}${endpoint}`;
    console.log(`[${functionName}] Full URL: ${fullUrl}`);
    console.log(`[${functionName}] Query params:`, queryParams);

    // Use responseType: 'blob' to handle CSV file download
    // Create a new axios instance for blob responses to avoid header conflicts
    const blobClient = axios.create({
      baseURL: API_BASE_URL,
      responseType: 'blob',
      timeout: 60000 // 60 second timeout for large exports
    });

    const response = await blobClient.get(endpoint, {
      params: queryParams
    });
    
    console.log(`[${functionName}] CSV export successful`);
    console.log(`[${functionName}] Response status: ${response.status}`);
    
    // Create blob URL and trigger download
    const blob = new Blob([response.data], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Get filename from Content-Disposition header or use default
    const contentDisposition = response.headers['content-disposition'];
    let filename = 'SolarMonitor_Report.csv';
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    return { success: true, filename };
  } catch (error) {
    console.error(`[${functionName}] CSV export failed`);
    console.error(`[${functionName}] Error type:`, error.constructor.name);
    console.error(`[${functionName}] Error message:`, error.message);
    
    if (error.response) {
      console.error(`[${functionName}] Response status:`, error.response.status);
      console.error(`[${functionName}] Response data:`, error.response.data);
      
      // Try to parse error message from blob if it's an error response
      if (error.response.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const errorData = JSON.parse(text);
          throw new Error(errorData.message || 'Failed to export CSV report');
        } catch (e) {
          // If it's not JSON, it might be HTML (404 page) or plain text
          if (error.response.status === 404) {
            throw new Error('CSV export endpoint not found (404). Please ensure the backend is running on http://localhost:5000 and has been restarted to include the new endpoint.');
          }
          throw new Error('Failed to export CSV report');
        }
      } else if (error.response.status === 404) {
        throw new Error('CSV export endpoint not found (404). Please ensure the backend is running and has been restarted.');
      } else if (error.response.status === 500) {
        throw new Error('Server error while generating CSV report. Please check the backend logs.');
      } else {
        throw new Error(error.response.data?.message || `Failed to export CSV report (Status: ${error.response.status})`);
      }
    } else if (error.request) {
      console.error(`[${functionName}] No response received. Request details:`, error.request);
      throw new Error('No response from server. Please ensure the backend API is running on http://localhost:5000');
    }
    
    console.error(`[${functionName}] Full error object:`, error);
    throw new Error(error.message || 'Failed to export CSV report. Please check your connection and try again.');
  }
};

export const fetchHistoricalData = async (params = {}) => {
  const functionName = 'fetchHistoricalData';
  const endpoint = '/solarmonitor/history';
  
  try {
    console.log(`[${functionName}] Starting API call to: ${endpoint}`);
    console.log(`[${functionName}] Parameters:`, params);
    
    const queryParams = {};
    if (params.organizationId !== undefined && params.organizationId !== null) {
      queryParams.organizationId = params.organizationId;
    }
    if (params.startDate) {
      queryParams.startDate = params.startDate;
    }
    if (params.endDate) {
      queryParams.endDate = params.endDate;
    }
    if (params.limit !== undefined && params.limit !== null) {
      queryParams.limit = params.limit;
    }
    if (params.startTime) {
      queryParams.startTime = params.startTime;
    }
    if (params.endTime) {
      queryParams.endTime = params.endTime;
    }

    const response = await apiClient.get(endpoint, { params: queryParams });
    
    console.log(`[${functionName}] API call successful`);
    console.log(`[${functionName}] Response status: ${response.status}`);
    console.log(`[${functionName}] Response data received:`, response.data);
    
    return response.data;
  } catch (error) {
    console.error(`[${functionName}] API call failed`);
    console.error(`[${functionName}] Error type:`, error.constructor.name);
    console.error(`[${functionName}] Error message:`, error.message);
    
    if (error.response) {
      console.error(`[${functionName}] Response status:`, error.response.status);
      console.error(`[${functionName}] Response data:`, error.response.data);
    } else if (error.request) {
      console.error(`[${functionName}] No response received. Request details:`, error.request);
    }
    
    console.error(`[${functionName}] Full error object:`, error);
    throw error;
  }
};

export default apiClient;

